import * as React from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import CustomReportViewModel from './ViewModels/CustomReportViewModel';
import { ToggleHeadCell } from './ViewModels/ReportDefinitions';
import ReportComponentViewModel from './ViewModels/ReportComponentViewModel';
import ReportSafetyDeviceViewModel from './ViewModels/ReportSafetyDeviceViewModel';
import ReportCredibleDamageViewModel from './ViewModels/ReportCredibleDamageViewModel';
import { SmallTableCell } from '../Utilities/ResponsiveTable';
import TableHead from '@mui/material/TableHead';
import Table from '@mui/material/Table';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import TableBody from '@mui/material/TableBody';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface IProps {
    row: CustomReportViewModel;
    headCells: ToggleHeadCell<CustomReportViewModel>[];
    compCells: ToggleHeadCell<ReportComponentViewModel>[];
    safetyCells: ToggleHeadCell<ReportSafetyDeviceViewModel>[];
    damageCells: ToggleHeadCell<ReportCredibleDamageViewModel>[];
}

export default function CustomReportRow({ row, headCells, compCells, safetyCells, damageCells }: IProps) {
    const [open, setOpen] = React.useState(false);

    const showDetails = React.useMemo(() => {
        return compCells.findIndex(f => f.enabled === true) > -1 ||
            damageCells.findIndex(f => f.enabled === true) > -1 ||
            safetyCells.findIndex(f => f.enabled === true) > -1;
    }, [compCells, safetyCells, damageCells]);

    return (
        <React.Fragment>
            <TableRow sx={showDetails ? { '& > *': { borderBottom: 'unset' } } : {}}>
                {showDetails &&
                    <TableCell>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}
                        >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>
                }
                {headCells.map((item, index) =>
                    <TableCell key={"reportRow" + index}>{row[item.id]?.toString()}</TableCell>
                )}
            </TableRow>
            {showDetails &&
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={headCells.length + 1}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            {(compCells.findIndex(f => f.enabled === true) > -1 && row.components.length > 0) &&
                                <Box sx={{ margin: 1 }}>
                                    <Typography variant="h6" gutterBottom component="div">
                                        Components
                                    </Typography>
                                    <Table size="small" aria-label="components">
                                        <TableHead>
                                            <TableRow>
                                                {compCells.map((headCell) =>
                                                    <SmallTableCell
                                                        key={headCell.id as string}
                                                        align={headCell.align}
                                                        padding={headCell.disablePadding ? 'none' : 'normal'}
                                                    >
                                                        {headCell.label}
                                                    </SmallTableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {row.components.map((compRow, index) =>
                                                <TableRow key={'compRow' + index}>
                                                    {compCells.map((item, index) =>
                                                        <TableCell key={"compRowCell" + index}>{compRow[item.id]?.toString()}</TableCell>
                                                    )}
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Box>
                            }
                            {(safetyCells.findIndex(f => f.enabled === true) > -1 && row.safetyDevices.length > 0) &&
                                <Box sx={{ margin: 1 }}>
                                    <Typography variant="h6" gutterBottom component="div">
                                        Safety Devices
                                    </Typography>
                                    <Table size="small" aria-label="safety devices">
                                        <TableHead>
                                            <TableRow>
                                                {safetyCells.map((headCell) =>
                                                    <SmallTableCell
                                                        key={headCell.id as string}
                                                        align={headCell.align}
                                                        padding={headCell.disablePadding ? 'none' : 'normal'}
                                                    >
                                                        {headCell.label}
                                                    </SmallTableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {row.safetyDevices.map((safetyRow, index) =>
                                                <TableRow key={'safetyRow' + index}>
                                                    {safetyCells.map((item, index) =>
                                                        <TableCell key={"safetyRowCell" + index}>{safetyRow[item.id]?.toString()}</TableCell>
                                                    )}
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Box>
                            }
                            {(damageCells.findIndex(f => f.enabled === true) > -1 && row.damageMechs.length > 0) &&
                                <Box sx={{ margin: 1 }}>
                                    <Typography variant="h6" gutterBottom component="div">
                                        Damage Mechanisms
                                    </Typography>
                                    <Table size="small" aria-label="damage mechanisms">
                                        <TableHead>
                                            <TableRow>
                                                {damageCells.map((headCell) =>
                                                    <SmallTableCell
                                                        key={headCell.id as string}
                                                        align={headCell.align}
                                                        padding={headCell.disablePadding ? 'none' : 'normal'}
                                                    >
                                                        {headCell.label}
                                                    </SmallTableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {row.damageMechs.map((damageRow, index) =>
                                                <TableRow key={'damageRow' + index}>
                                                    {damageCells.map((item, index) =>
                                                        <TableCell key={"damageRowCell" + index}>{damageRow[item.id]?.toString()}</TableCell>
                                                    )}
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Box>
                            }
                        </Collapse>
                    </TableCell>
                </TableRow>
            }
        </React.Fragment>
    );
}